<template>
  <div class='bubblemap' ref="container">
    <div class="bubblemap__controls">
      <div class="bubblemap__view-toggle" role="radiogroup" aria-label="Map view mode">
        <button
          :class="['bubblemap__view-btn', { 'bubblemap__view-btn--active': viewMode === '2d' }]"
          @click="switchView('2d')"
          aria-label="2D flat map view"
        >2D</button>
        <button
          :class="['bubblemap__view-btn', { 'bubblemap__view-btn--active': viewMode === '3d' }]"
          @click="switchView('3d')"
          aria-label="3D globe view"
        >3D</button>
      </div>
      <div class="bubblemap__zoom-controls">
        <button
          class="bubblemap__zoom-btn"
          @click="zoomIn"
          aria-label="Zoom in"
        >+</button>
        <button
          class="bubblemap__zoom-btn"
          @click="zoomOut"
          aria-label="Zoom out"
        >−</button>
      </div>
    </div>
    <svg
      ref="svg"
      :width="width"
      :height="height"
      class="bubblemap__svg"
    ></svg>
    <div ref="tooltip" class="bubblemap__tooltip" />
  </div>
</template>

<script>
import * as d3 from 'd3';
import WorldGeoJson from '@/assets/maps/world.geojson';

export default {
  name: 'Bubblemap',
  props: {
    data: {
      type: Array,
      required: false,
      default: () => [],
    },
    width: {
      default: 900,
      type: Number,
    },
    height: {
      default: 520,
      type: Number,
    },
  },
  data() {
    return {
      viewMode: '3d',
      rotation: [0, -20, 0],
      geoData: null,
      themeObserver: null,
      zoomLevel: 1,
      projection: null,
      pathGenerator: null,
      flatZoom: null,
      flatSvg: null,
    };
  },
  watch: {
    data() {
      this.cleanup();
      this.buildMap();
    },
  },
  mounted() {
    this.geoData = JSON.parse(WorldGeoJson);
    this.buildMap();
    this.observeTheme();
  },
  beforeDestroy() {
    this.cleanup();
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  },
  methods: {
    cleanup() {
      const svg = d3.select(this.$refs.svg);
      svg.selectAll('*').remove();
      svg.on('.zoom', null);
      svg.on('.drag', null);
    },

    observeTheme() {
      this.themeObserver = new MutationObserver(() => {
        this.cleanup();
        this.buildMap();
      });
      this.themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-theme'],
      });
    },

    getThemeColors() {
      const style = getComputedStyle(this.$refs.container);
      const v = name => style.getPropertyValue(name).trim();
      return {
        bg: v('--map-bg') || '#0a0b14',
        ocean: v('--map-ocean') || '#0d1b2a',
        oceanMid: v('--map-ocean-mid') || '#0a1628',
        oceanEdge: v('--map-ocean-edge') || '#060e1a',
        land: v('--map-land') || '#162a3a',
        landHover: v('--map-land-hover') || '#1e3a4a',
        border: v('--map-border') || 'rgba(0, 200, 180, 0.25)',
        grid: v('--map-grid') || 'rgba(0, 200, 255, 0.06)',
        atmosStroke: v('--map-atmos-stroke') || 'rgba(0, 200, 255, 0.08)',
        atmosGlow: v('--map-atmos-glow') || 'rgba(0, 200, 255, 0.15)',
      };
    },

    switchView(mode) {
      if (this.viewMode === mode) return;
      this.viewMode = mode;
      this.zoomLevel = 1;
      this.cleanup();
      this.buildMap();
    },

    zoomIn() {
      if (this.viewMode === '2d' && this.flatZoom && this.flatSvg) {
        this.flatZoom.scaleBy(this.flatSvg.transition().duration(300), 1.5);
      } else {
        this.zoomLevel = Math.min(this.zoomLevel * 1.3, 5);
        this.applyZoom();
      }
    },

    zoomOut() {
      if (this.viewMode === '2d' && this.flatZoom && this.flatSvg) {
        this.flatZoom.scaleBy(this.flatSvg.transition().duration(300), 0.67);
      } else {
        this.zoomLevel = Math.max(this.zoomLevel / 1.3, 0.5);
        this.applyZoom();
      }
    },

    applyZoom() {
      if (!this.projection || this.viewMode !== '3d') return;
      const svg = d3.select(this.$refs.svg);
      const baseScale = Math.min(this.width, this.height) / 2.2;

      this.projection.scale(baseScale * this.zoomLevel);
      this.pathGenerator.projection(this.projection);
      svg.selectAll('.bubblemap__countries path')
        .attr('d', this.pathGenerator);
      svg.select('.bubblemap__graticule')
        .attr('d', this.pathGenerator);
      svg.select('.bubblemap__ocean')
        .attr('r', this.projection.scale());
      svg.select('.bubblemap__atmosphere')
        .attr('r', this.projection.scale() + 8);
      this.updateMarkerPositions(svg, this.projection, true);
    },

    buildMap() {
      if (this.viewMode === '3d') {
        this.buildGlobe();
      } else {
        this.buildFlat();
      }
    },

    buildGlobe() {
      const svg = d3.select(this.$refs.svg);
      const tooltip = d3.select(this.$refs.tooltip);
      const {
        width, height, data, geoData,
      } = this;
      const sensitivity = 75;
      const c = this.getThemeColors();

      const projection = d3.geoOrthographic()
        .scale(Math.min(width, height) / 2.2)
        .center([0, 0])
        .rotate(this.rotation)
        .translate([width / 2, height / 2]);

      const pathGenerator = d3.geoPath().projection(projection);

      // Defs for glow effects and sphere gradient
      const defs = svg.append('defs');

      // Ocean gradient
      const oceanGrad = defs.append('radialGradient')
        .attr('id', 'ocean-gradient')
        .attr('cx', '50%')
        .attr('cy', '50%')
        .attr('r', '50%');
      oceanGrad.append('stop')
        .attr('offset', '0%')
        .attr('stop-color', c.ocean);
      oceanGrad.append('stop')
        .attr('offset', '85%')
        .attr('stop-color', c.oceanMid);
      oceanGrad.append('stop')
        .attr('offset', '100%')
        .attr('stop-color', c.oceanEdge);

      // Glow filter for markers
      const glowFilter = defs.append('filter')
        .attr('id', 'marker-glow')
        .attr('x', '-50%')
        .attr('y', '-50%')
        .attr('width', '200%')
        .attr('height', '200%');
      glowFilter.append('feGaussianBlur')
        .attr('stdDeviation', '3')
        .attr('result', 'coloredBlur');
      const feMerge = glowFilter.append('feMerge');
      feMerge.append('feMergeNode').attr('in', 'coloredBlur');
      feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

      // Atmosphere glow
      const atmosFilter = defs.append('filter')
        .attr('id', 'atmosphere-glow')
        .attr('x', '-20%')
        .attr('y', '-20%')
        .attr('width', '140%')
        .attr('height', '140%');
      atmosFilter.append('feGaussianBlur')
        .attr('stdDeviation', '12')
        .attr('result', 'blur');
      const atmosMerge = atmosFilter.append('feMerge');
      atmosMerge.append('feMergeNode').attr('in', 'blur');
      atmosMerge.append('feMergeNode').attr('in', 'SourceGraphic');

      // Atmosphere ring
      svg.append('circle')
        .attr('class', 'bubblemap__atmosphere')
        .attr('cx', width / 2)
        .attr('cy', height / 2)
        .attr('r', projection.scale() + 8)
        .attr('fill', 'none')
        .attr('stroke', c.atmosStroke)
        .attr('stroke-width', 16)
        .attr('filter', 'url(#atmosphere-glow)');

      // Ocean sphere
      svg.append('circle')
        .attr('class', 'bubblemap__ocean')
        .attr('cx', width / 2)
        .attr('cy', height / 2)
        .attr('r', projection.scale())
        .attr('fill', 'url(#ocean-gradient)')
        .attr('stroke', c.atmosGlow)
        .attr('stroke-width', 0.5);

      // Graticule
      const graticule = d3.geoGraticule().step([15, 15]);
      svg.append('path')
        .datum(graticule)
        .attr('d', pathGenerator)
        .attr('fill', 'none')
        .attr('stroke', c.grid)
        .attr('stroke-width', 0.4)
        .attr('class', 'bubblemap__graticule');

      // Countries
      const countriesGroup = svg.append('g')
        .attr('class', 'bubblemap__countries');
      countriesGroup.selectAll('path')
        .data(geoData.features)
        .join('path')
        .attr('d', pathGenerator)
        .attr('fill', c.land)
        .attr('stroke', c.border)
        .attr('stroke-width', 0.5)
        .on('mouseenter', event => {
          d3.select(event.currentTarget).attr('fill', c.landHover);
        })
        .on('mousemove', (event, d) => {
          tooltip
            .style('opacity', 1)
            .html(`<span class="bubblemap__tooltip-country">${d.properties.name}</span>`)
            .style('left', `${event.offsetX + 12}px`)
            .style('top', `${event.offsetY - 28}px`);
        })
        .on('mouseleave', event => {
          d3.select(event.currentTarget).attr('fill', c.land);
          tooltip.style('opacity', 0);
        });

      // Markers
      this.renderMarkers(svg, projection, data, tooltip, true);

      // Drag to rotate
      const self = this;
      svg.call(d3.drag()
        .on('drag', event => {
          const rotate = projection.rotate();
          const k = sensitivity / projection.scale();
          projection.rotate([
            rotate[0] + event.dx * k,
            Math.max(-90, Math.min(90, rotate[1] - event.dy * k)),
            rotate[2],
          ]);
          self.rotation = projection.rotate();
          pathGenerator.projection(projection);
          countriesGroup.selectAll('path').attr('d', pathGenerator);
          svg.select('.bubblemap__graticule').attr('d', pathGenerator);
          self.updateMarkerPositions(svg, projection, true);
        }));

      // Scroll to zoom
      svg.on('wheel', event => {
        event.preventDefault();
        const direction = event.deltaY > 0 ? -1 : 1;
        this.zoomLevel = Math.max(0.5, Math.min(5, this.zoomLevel + direction * 0.15));
        this.applyZoom();
      });

      // Store refs for zoom
      this.projection = projection;
      this.pathGenerator = pathGenerator;
    },

    buildFlat() {
      const svg = d3.select(this.$refs.svg);
      const tooltip = d3.select(this.$refs.tooltip);
      const {
        width, height, data, geoData,
      } = this;
      const c = this.getThemeColors();

      const projection = d3.geoNaturalEarth1()
        .fitSize([width - 40, height - 40], { type: 'Sphere' })
        .translate([width / 2, height / 2]);

      const pathGenerator = d3.geoPath().projection(projection);

      // Defs
      const defs = svg.append('defs');
      const glowFilter = defs.append('filter')
        .attr('id', 'marker-glow-2d')
        .attr('x', '-50%')
        .attr('y', '-50%')
        .attr('width', '200%')
        .attr('height', '200%');
      glowFilter.append('feGaussianBlur')
        .attr('stdDeviation', '3')
        .attr('result', 'coloredBlur');
      const feMerge = glowFilter.append('feMerge');
      feMerge.append('feMergeNode').attr('in', 'coloredBlur');
      feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

      // Everything zoomable goes in this group
      const zoomGroup = svg.append('g')
        .attr('class', 'bubblemap__zoom-group');

      // Background sphere outline
      zoomGroup.append('path')
        .datum({ type: 'Sphere' })
        .attr('d', pathGenerator)
        .attr('class', 'bubblemap__sphere')
        .attr('fill', c.oceanMid)
        .attr('stroke', c.atmosGlow)
        .attr('stroke-width', 0.5);

      // Graticule
      const graticule = d3.geoGraticule().step([20, 20]);
      zoomGroup.append('path')
        .datum(graticule)
        .attr('d', pathGenerator)
        .attr('fill', 'none')
        .attr('stroke', c.grid)
        .attr('stroke-width', 0.3)
        .attr('class', 'bubblemap__graticule-2d');

      // Countries
      const countriesGroup = zoomGroup.append('g')
        .attr('class', 'bubblemap__countries');
      countriesGroup.selectAll('path')
        .data(geoData.features)
        .join('path')
        .attr('d', pathGenerator)
        .attr('fill', c.land)
        .attr('stroke', c.border)
        .attr('stroke-width', 0.4)
        .on('mouseenter', event => {
          d3.select(event.currentTarget).attr('fill', c.landHover);
        })
        .on('mousemove', (event, d) => {
          tooltip
            .style('opacity', 1)
            .html(`<span class="bubblemap__tooltip-country">${d.properties.name}</span>`)
            .style('left', `${event.offsetX + 12}px`)
            .style('top', `${event.offsetY - 28}px`);
        })
        .on('mouseleave', event => {
          d3.select(event.currentTarget).attr('fill', c.land);
          tooltip.style('opacity', 0);
        });

      // Markers go in the zoom group too
      this.renderMarkers(zoomGroup, projection, data, tooltip, false);

      // D3 zoom with pan — zooms toward cursor position
      const zoom = d3.zoom()
        .scaleExtent([1, 8])
        .on('zoom', event => {
          zoomGroup.attr('transform', event.transform);
        });
      svg.call(zoom);

      // Store zoom behavior for button controls
      this.flatZoom = zoom;
      this.flatSvg = svg;

      // Store refs
      this.projection = projection;
      this.pathGenerator = pathGenerator;
    },

    renderMarkers(svg, projection, data, tooltip, isGlobe) {
      if (!data || !data.length) return;

      const valueExtent = d3.extent(data, d => +d.count);
      const size = d3.scaleSqrt()
        .domain(valueExtent[0] === valueExtent[1] ? [0, valueExtent[1]] : valueExtent)
        .range([3, 22]);

      // Cyan → Amber → Red heat palette
      const colorScale = d3.scaleLinear()
        .domain([
          valueExtent[0] || 0,
          ((valueExtent[0] || 0) + (valueExtent[1] || 1)) / 2,
          valueExtent[1] || 1,
        ])
        .range(['#00d4ff', '#ffb020', '#ff3355'])
        .clamp(true);

      const filterId = isGlobe ? 'marker-glow' : 'marker-glow-2d';
      const sortedData = [...data].sort((a, b) => +b.count - +a.count);

      sortedData.forEach((d, i) => {
        const coords = projection([+d.longitude, +d.latitude]);
        if (!coords) return;

        // For globe, hide markers on the back side
        const isVisible = !isGlobe || this.isPointVisible(projection, d);
        const r = size(+d.count);
        const color = colorScale(+d.count);

        const group = svg.append('g')
          .attr('class', 'bubblemap__marker-group')
          .attr('data-lon', d.longitude)
          .attr('data-lat', d.latitude)
          .attr('data-cx', coords[0])
          .attr('data-cy', coords[1])
          .style('opacity', isVisible ? 1 : 0);

        // Outer pulse ring
        group.append('circle')
          .attr('cx', coords[0])
          .attr('cy', coords[1])
          .attr('r', r + 6)
          .attr('fill', 'none')
          .attr('stroke', color)
          .attr('stroke-width', 1.5)
          .attr('opacity', 0.5)
          .style('pointer-events', 'none')
          .attr('class', `bubblemap__pulse bubblemap__pulse--delay-${i % 3}`);

        // Glow circle with border ring for cluster separation
        group.append('circle')
          .attr('cx', coords[0])
          .attr('cy', coords[1])
          .attr('r', r)
          .attr('fill', color)
          .attr('fill-opacity', 0.3)
          .attr('stroke', color)
          .attr('stroke-width', 1.5)
          .attr('stroke-opacity', 0.7)
          .style('pointer-events', 'none')
          .attr('filter', `url(#${filterId})`);

        // Core dot
        group.append('circle')
          .attr('cx', coords[0])
          .attr('cy', coords[1])
          .attr('r', Math.max(r * 0.4, 2.5))
          .attr('fill', color)
          .attr('fill-opacity', 1)
          .style('pointer-events', 'none');

        // Hover target (invisible, larger)
        group.append('circle')
          .attr('cx', coords[0])
          .attr('cy', coords[1])
          .attr('r', Math.max(r + 6, 10))
          .attr('fill', 'transparent')
          .attr('cursor', 'pointer')
          .on('mouseenter', event => {
            const countryName = d.countryName || d.country || this.findCountryName(d) || '';
            tooltip
              .style('opacity', 1)
              .html(
                '<div class="bubblemap__tooltip-marker">' +
                `${countryName ? `<div class="bubblemap__tooltip-name">${countryName}</div>` : ''}` +
                `<div class="bubblemap__tooltip-count">${(+d.count).toLocaleString()} IPs</div>` +
                `<div class="bubblemap__tooltip-coords">${(+d.latitude).toFixed(2)}°, ${(+d.longitude).toFixed(2)}°</div>` +
                '</div>',
              )
              .style('left', `${event.offsetX + 14}px`)
              .style('top', `${event.offsetY - 40}px`);
          })
          .on('mouseleave', () => {
            tooltip.style('opacity', 0);
          });
      });
    },

    isPointVisible(projection, d) {
      const rotate = projection.rotate();
      const center = [-rotate[0], -rotate[1]];
      const point = [+d.longitude, +d.latitude];
      return d3.geoDistance(center, point) < Math.PI / 2;
    },

    findCountryName(d) {
      if (!this.geoData) return '';
      const point = [+d.longitude, +d.latitude];
      const feature = this.geoData.features.find(
        f => d3.geoContains(f, point),
      );
      return feature ? feature.properties.name : '';
    },

    updateMarkerPositions(svg, projection, isGlobe) {
      svg.selectAll('.bubblemap__marker-group').each((datum, idx, nodes) => {
        const group = d3.select(nodes[idx]);
        const lon = +group.attr('data-lon');
        const lat = +group.attr('data-lat');
        const coords = projection([lon, lat]);

        let visible = true;
        if (isGlobe) {
          const rotate = projection.rotate();
          const center = [-rotate[0], -rotate[1]];
          visible = d3.geoDistance(center, [lon, lat]) < Math.PI / 2;
        }

        group.style('opacity', visible ? 1 : 0);
        group.selectAll('circle')
          .attr('cx', coords[0])
          .attr('cy', coords[1]);
      });
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.bubblemap {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--map-bg, #0a0b14);
  border-radius: $radius-lg;
  overflow: hidden;

  &__svg {
    display: block;
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }

  &__controls {
    position: absolute;
    top: $space-3;
    right: $space-3;
    z-index: 10;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: $space-2;
  }

  &__zoom-controls {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__zoom-btn {
    @include flex-center;
    width: $space-7;
    height: $space-7;
    border: 1px solid var(--border-primary, rgba(255, 255, 255, 0.08));
    border-radius: $radius-md;
    background: var(--bg-tertiary, rgba(255, 255, 255, 0.06));
    color: var(--text-secondary, #9ba4b8);
    font-size: $font-size-lg;
    line-height: 1;
    cursor: pointer;
    @include transition(all, $transition-duration-fast);

    &:hover {
      background: rgba(0, 200, 255, 0.12);
      color: var(--map-tooltip-count, #00c8ff);
    }
  }

  &__view-toggle {
    display: flex;
    background: var(--bg-tertiary, rgba(255, 255, 255, 0.06));
    border-radius: $radius-md;
    border: 1px solid var(--border-primary, rgba(255, 255, 255, 0.08));
    overflow: hidden;
  }

  &__view-btn {
    padding: $space-1 $space-3;
    border: none;
    background: transparent;
    color: var(--text-tertiary, #6b7590);
    font-family: $font-family-semibold;
    font-size: $font-size-xs;
    cursor: pointer;
    letter-spacing: $letter-spacing-wide;
    @include transition(all, $transition-duration-fast);

    &:hover {
      color: var(--text-secondary, #9ba4b8);
    }

    &--active {
      background: var(--map-tooltip-count, rgba(0, 200, 255, 0.15));
      background: rgba(0, 200, 255, 0.15);
      color: var(--map-tooltip-count, #00c8ff);
    }
  }

  &__tooltip {
    position: absolute;
    pointer-events: none;
    opacity: 0;
    background: var(--map-tooltip-bg, #222638);
    color: var(--map-tooltip-name, #f0f0f5);
    padding: $space-2 $space-3;
    border-radius: $radius-6;
    font-family: $font-family-regular;
    font-size: $font-size-xs;
    line-height: $line-height-snug;
    border: 1px solid var(--map-tooltip-border, rgba(0, 200, 255, 0.15));
    box-shadow: 0 4px 20px var(--map-tooltip-shadow, rgba(0, 0, 0, 0.4));
    @include transition(opacity, 120ms);
    z-index: 20;
    white-space: nowrap;
  }
}
</style>

<style lang="scss">
@import '~@/assets/styles/design-system';

.bubblemap__tooltip-country {
  font-family: $font-family-semibold;
  font-size: $font-size-sm;
  color: var(--map-tooltip-name, #f0f0f5) !important;
}

.bubblemap__tooltip-marker {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.bubblemap__tooltip-name {
  font-family: $font-family-semibold;
  font-size: $font-size-sm;
  color: var(--map-tooltip-name, #f0f0f5) !important;
}

.bubblemap__tooltip-count {
  font-family: $font-family-bold;
  font-size: $font-size-sm;
  color: var(--map-tooltip-count, #00c8ff) !important;
}

.bubblemap__tooltip-coords {
  font-family: $font-family-regular;
  font-size: $font-size-2xs;
  color: var(--map-tooltip-coords, #6b7590) !important;
}

@keyframes marker-pulse {
  0% {
    r: inherit;
    opacity: 0.6;
  }
  100% {
    r: 36;
    opacity: 0;
  }
}

.bubblemap__pulse {
  animation: marker-pulse 4s ease-out infinite;

  &--delay-0 { animation-delay: 0s; }
  &--delay-1 { animation-delay: 1.3s; }
  &--delay-2 { animation-delay: 2.6s; }
}
</style>
